/* eslint-disable @khanacademy/ts-no-error-suppressions */
/**
 * Component rendering the footer CTA on the SAT landing page.
 */

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {MediaLayout} from "@khanacademy/wonder-blocks-layout";
import {useMarkConversion} from "@khan/analytics-learning-time";

type Props = {
    // The URL that the CTA button should link to.
    targetUrl: string | null | undefined;
    title: React.ReactNode;
};

export default function CtaModule({
    targetUrl,
    title,
}: Props): React.ReactElement {
    const markConversion = useMarkConversion();

    return (
        <MediaLayout styleSheets={styleSheets}>
            {({styles}) => (
                <View style={styles.content}>
                    <View style={styles.moduleBody}>
                        <h1
                            // This is a <h1> instead of a Wonder Blocks typography
                            // component because the font size does not conform to
                            // Wonder Blocks specifications, since we are aligning
                            // the style of this page with College Board's branding.
                            // @ts-expect-error - TS2345 - Argument of type 'StyleType' is not assignable to parameter of type 'CSSProperties | Falsy'.
                            className={css(styles.title)}
                        >
                            {title}
                        </h1>
                        <View style={styles.buttonContainer}>
                            <Button
                                spinner={targetUrl == null}
                                href={targetUrl ?? ""}
                                safeWithNav={() =>
                                    markConversion({
                                        id: "sat_landing_page_cta_click",
                                        extra: {
                                            Product: "test-prep",
                                            Domain: "test-prep",
                                            Subject: "sat",
                                        },
                                    })
                                }
                                size="large"
                                style={styles.ctaButton}
                            >
                                {t`Let's go`}
                            </Button>
                        </View>
                    </View>
                </View>
            )}
        </MediaLayout>
    );
}

const MOBILE_MAX_WIDTH = 312;

const styleSheets = {
    all: StyleSheet.create({
        buttonContainer: {
            marginTop: 40,
        },
        moduleBody: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 24,
            marginLeft: 24,
        },
        title: {
            color: "black",
            fontFamily: "Source Serif Pro",
            fontWeight: 600,
            "-webkit-font-smoothing": "antialiased",
            "-moz-osx-font-smoothing": "grayscale",
            marginBottom: 0,
            textAlign: "center",
        },
    }),
    mdOrLarger: StyleSheet.create({
        content: {
            paddingTop: 143,
            paddingBottom: 108,
        },
        ctaButton: {
            width: 176,
        },
        title: {
            fontSize: 44,
            lineHeight: "48px",
            maxWidth: 860,
        },
    }),
    small: StyleSheet.create({
        // Note(nisha): The following styles are used. The linter is confused
        // on the last style so we have to disable eslint.
        // eslint-disable-next-line react-native/no-unused-styles
        content: {
            paddingTop: 64,
            paddingBottom: 64,
        },
        // eslint-disable-next-line react-native/no-unused-styles
        backgroundSparkle: {
            display: "none",
        },
        // eslint-disable-next-line react-native/no-unused-styles
        backgroundPencil: {
            display: "none",
        },
        // eslint-disable-next-line react-native/no-unused-styles
        buttonContainer: {
            flexDirection: "column",
            width: "100%",
            maxWidth: MOBILE_MAX_WIDTH,
        },
        // eslint-disable-next-line react-native/no-unused-styles
        title: {
            fontSize: 30,
            lineHeight: "36px",
            maxWidth: MOBILE_MAX_WIDTH,
        },
    }),
} as const;
